import { useState } from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';

import { CreateAccountRecommendationExplanationDocument } from '../graphql/mutation.createAccountRecommendationExplanation.generated';
import {
  AccountRecommendationOptionDocument,
  AccountRecommendationOptionQuery,
} from '../graphql/query.accountRecommendationOption.generated';

type AccountRecommendationOption = NonNullable<AccountRecommendationOptionQuery['accountRecommendationOption']>;
type AccountRecommendation = NonNullable<AccountRecommendationOption['recommendation']>;
type ProductOnboardingOption = AccountRecommendation['productOnboardingOption'];

type RecommendationState = {
  id: string;
  option: ProductOnboardingOption;
  isSkipEligible: boolean;
  explanation: string | null;
  optionDisclosure?: string | null;
};

export const useAccountRecommendation = (props: { initialRecommendationOption: AccountRecommendationOption }) => {
  const { initialRecommendationOption } = props;

  const [isExplanationLoading, setIsExplanationLoading] = useState<boolean>(false);
  const [isSkipLoading, setIsSkipLoading] = useState<boolean>(false);
  const [currentError, setError] = useState<Error | null>(null);
  const [currentRecommendation, setCurrentRecommendation] = useState<RecommendationState>({
    id: initialRecommendationOption.recommendation.id,
    option: initialRecommendationOption.recommendation.productOnboardingOption,
    isSkipEligible: initialRecommendationOption.recommendation.isSkipEligible,
    explanation: null,
    optionDisclosure: initialRecommendationOption.recommendation.optionDisclosure ?? null,
  });

  const { refetch: fetchRecommendation } = useGraphQLQuery(AccountRecommendationOptionDocument, { skip: true });
  const [generateExplanationMutation] = useGraphQLMutation(CreateAccountRecommendationExplanationDocument);

  if (currentError) {
    throw currentError;
  }

  const fetchExplanation = async (recommendationId: string): Promise<string | null> => {
    const result = await generateExplanationMutation({ variables: { recommendationId } });

    const explanation = result.data?.createAccountRecommendationExplanation.result;
    const errors = result.data?.createAccountRecommendationExplanation.displayableErrors;

    if (errors && errors.length > 0) {
      const errorMessage = errors.map(e => e.message).join(', ');
      throw new Error(`Failed to generate explanation: ${errorMessage}`);
    }

    return explanation ?? null;
  };

  const generateExplanation = async (): Promise<void> => {
    setIsExplanationLoading(true);
    try {
      const explanation = await fetchExplanation(currentRecommendation.id);

      if (explanation) {
        setCurrentRecommendation({ ...currentRecommendation, explanation });
      }
    } catch (error) {
      setError(Error(`Failed to generate explanation: ${error?.toString()}.`));
    } finally {
      setIsExplanationLoading(false);
    }
  };

  const skipAccountRecommendation = async (): Promise<void> => {
    setIsSkipLoading(true);
    const { explanation: previousExplanation, id } = currentRecommendation;

    try {
      const result = await fetchRecommendation({ skippedRecommendation: id });
      const accountRecommendationOption = result.data?.accountRecommendationOption;

      if (accountRecommendationOption) {
        const nextRecommendation = accountRecommendationOption.recommendation;
        let nextExplanation: string | null = null;

        if (previousExplanation) {
          try {
            nextExplanation = await fetchExplanation(nextRecommendation.id);
          } catch {
            // Gracefully allow no explanation if explanation fails
          }
        }

        setCurrentRecommendation({
          id: nextRecommendation.id,
          option: nextRecommendation.productOnboardingOption,
          isSkipEligible: nextRecommendation.isSkipEligible,
          explanation: nextExplanation,
          optionDisclosure: nextRecommendation.optionDisclosure ?? null,
        });
      }
    } catch (error) {
      setError(Error(`Failed to fetch next recommendation: ${error?.toString()}.`));
    } finally {
      setIsSkipLoading(false);
    }
  };

  const isLoading = isExplanationLoading || isSkipLoading;

  return {
    currentRecommendation,
    isExplanationLoading,
    isSkipLoading,
    generateExplanation: isLoading ? null : generateExplanation,
    skipAccountRecommendation: isLoading ? null : skipAccountRecommendation,
  };
};
